import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";

import { brand, brandAssetPath } from "@shared/config";
import { isRTL, supportedLangs } from "@shared/i18n";
import { removeUnownedHeadDuplicates } from "@shared/seo/headDedupe";

import { localizedRoot, resolveLang } from "../lib/links";
import { useEcosystemCategories } from "../ecosystem-section/featureCategories";
import { FAQ_ITEMS } from "../faq-section/faqItems";

export function StructuredData() {
  const { t, i18n } = useTranslation("landing");
  const lang = resolveLang(i18n.resolvedLanguage);
  const { categories: visibleCategories } = useEcosystemCategories();
  const origin = typeof window === "undefined" ? "" : window.location.origin;
  const canonicalUrl = `${origin}${localizedRoot(lang)}`;
  const alternateUrls = supportedLangs.map((supportedLang) => ({
    lang: supportedLang,
    href: `${origin}${localizedRoot(supportedLang)}`,
  }));
  const assetBase = import.meta.env.BASE_URL;
  const ogImage = origin
    ? `${origin}/${brandAssetPath(assetBase, brand.assets.ogImage).replace(/^\//, "")}`
    : brandAssetPath(assetBase, brand.assets.ogImage);

  const faqSchema = FAQ_ITEMS.map((id) => ({
    "@type": "Question",
    name: t(`faq.items.${id}.question`),
    acceptedAnswer: {
      "@type": "Answer",
      text: t(`faq.items.${id}.answer`),
    },
  }));

  const featureList = visibleCategories.map((category) =>
    t(`ecosystem.categories.${category.key}.title`),
  );

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        name: t("schema.organizationName"),
        url: origin || canonicalUrl,
        logo: ogImage,
      },
      {
        "@type": "SoftwareApplication",
        name: t("schema.applicationName"),
        applicationCategory: t("schema.category"),
        operatingSystem: t("schema.operatingSystem"),
        isAccessibleForFree: true,
        url: canonicalUrl,
        description: t("seo.description"),
        featureList,
        // The app is free: AI features run on the visitor's own provider key,
        // so there is nothing for us to sell. A schema advertising a paid
        // product here would be both wrong and a rich-results liability.
        offers: {
          "@type": "Offer",
          price: "0",
          priceCurrency: "USD",
          availability: "https://schema.org/InStock",
        },
      },
      {
        "@type": "FAQPage",
        mainEntity: faqSchema,
      },
    ],
  };

  return (
    <Helmet onChangeClientState={() => removeUnownedHeadDuplicates()}>
      <html lang={lang} dir={isRTL(lang) ? "rtl" : "ltr"} />
      <title>{t("seo.title")}</title>
      <meta name="description" content={t("seo.description")} />
      <meta property="og:title" content={t("seo.title")} />
      <meta property="og:description" content={t("seo.description")} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={t("seo.ogAlt")} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={t("seo.title")} />
      <meta name="twitter:description" content={t("seo.description")} />
      <link rel="canonical" href={canonicalUrl} />
      {alternateUrls.map((alternate) => (
        <link
          key={alternate.lang}
          rel="alternate"
          hrefLang={alternate.lang}
          href={alternate.href}
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={`${origin}/`} />
      <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
    </Helmet>
  );
}

export const LandingSeo = StructuredData;
export default StructuredData;
